// Healthcare Provider types
export interface HealthcareProvider {
  id: string;
  name: string;
  type: HealthcareProviderType;
  description?: string;
  website?: string;
  email: string;
  phone?: string;
  address: string;
  city: string;
  country: string;
  postalCode: string;
  licenseNumber?: string;
  certifications: string[];
  accreditations: string[];
  services: string[];
  specialties: string[];
  languages: string[];
  isActive: boolean;
  isVerified: boolean;
  verifiedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export enum HealthcareProviderType {
  HOSPITAL = 'HOSPITAL',
  CLINIC = 'CLINIC',
  PHARMACY = 'PHARMACY',
  LABORATORY = 'LABORATORY',
  DOCTOR_OFFICE = 'DOCTOR_OFFICE',
  NURSING_HOME = 'NURSING_HOME',
  REHABILITATION_CENTER = 'REHABILITATION_CENTER',
  MENTAL_HEALTH_CENTER = 'MENTAL_HEALTH_CENTER',
  EMERGENCY_SERVICES = 'EMERGENCY_SERVICES',
  OTHER = 'OTHER',
}

// Tour Operator types
export interface TourOperator {
  id: string;
  name: string;
  type: TourOperatorType;
  description?: string;
  website?: string;
  email: string;
  phone?: string;
  address: string;
  city: string;
  country: string;
  postalCode: string;
  licenseNumber?: string;
  certifications: string[];
  destinations: string[];
  travelTypes: string[];
  languages: string[];
  isActive: boolean;
  isVerified: boolean;
  verifiedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export enum TourOperatorType {
  TRAVEL_AGENCY = 'TRAVEL_AGENCY',
  TOUR_OPERATOR = 'TOUR_OPERATOR', 
  CRUISE_LINE = 'CRUISE_LINE',
  AIRLINE = 'AIRLINE',
  HOTEL_CHAIN = 'HOTEL_CHAIN',
  CAR_RENTAL = 'CAR_RENTAL',
  ADVENTURE_TOURS = 'ADVENTURE_TOURS',
  CULTURAL_TOURS = 'CULTURAL_TOURS',
  BUSINESS_TRAVEL = 'BUSINESS_TRAVEL',
  OTHER = 'OTHER',
}

// Affiliate Partner types
export interface AffiliatePartner {
  id: string;
  name: string;
  type: AffiliatePartnerType;
  description?: string;
  website?: string;
  email: string;
  phone?: string;
  address: string;
  city: string;
  country: string;
  postalCode: string;
  licenseNumber?: string;
  certifications: string[];
  services: string[];
  products: string[];
  targetMarkets: string[];
  partnershipType: PartnershipType;
  contractStartDate: string;
  contractEndDate?: string;
  isActive: boolean;
  isVerified: boolean;
  verifiedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export enum AffiliatePartnerType {
  TRAVEL_INSURANCE = 'TRAVEL_INSURANCE',
  HEALTH_INSURANCE = 'HEALTH_INSURANCE',
  MEDICAL_EQUIPMENT = 'MEDICAL_EQUIPMENT',
  PHARMACEUTICAL = 'PHARMACEUTICAL',
  WELLNESS_SERVICES = 'WELLNESS_SERVICES',
  TELEMEDICINE = 'TELEMEDICINE',
  DIAGNOSTIC_SERVICES = 'DIAGNOSTIC_SERVICES',
  EMERGENCY_ASSISTANCE = 'EMERGENCY_ASSISTANCE',
  LOGISTICS_PROVIDER = 'LOGISTICS_PROVIDER',
  FINTECH = 'FINTECH',
  OTHER = 'OTHER',
}

export enum PartnershipType {
  REFERRAL = 'REFERRAL',
  COMMISSION_BASED = 'COMMISSION_BASED',
  REVENUE_SHARE = 'REVENUE_SHARE',
  WHITE_LABEL = 'WHITE_LABEL',
  RESELLER = 'RESELLER',
  TECHNOLOGY_PARTNER = 'TECHNOLOGY_PARTNER',
  CONTENT_PARTNER = 'CONTENT_PARTNER',
}

// Registration request types
export interface HealthcareProviderRegistrationRequest {
  // Provider info
  providerName: string;
  providerType: HealthcareProviderType;
  description?: string;
  website?: string;
  providerEmail: string;
  phone?: string;
  address: string;
  city: string;
  country: string;
  postalCode: string;
  licenseNumber?: string;
  certifications: string[];
  services: string[];
  specialties: string[];
  languages: string[];
  
  // Admin user info
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phoneNumber?: string;
}

export interface TourOperatorRegistrationRequest {
  // Operator info
  operatorName: string;
  operatorType: TourOperatorType;
  description?: string;
  website?: string;
  operatorEmail: string;
  phone?: string;
  address: string;
  city: string;
  country: string;
  postalCode: string;
  licenseNumber?: string;
  certifications: string[];
  destinations: string[];
  travelTypes: string[];
  languages: string[];
  
  // Admin user info
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phoneNumber?: string;
}

// Simplified Tour Operator Admin Registration
export interface TourOperatorAdminRegistrationRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  companyName: string;
  operatorType?: TourOperatorType;
  address?: string;
  phone?: string;
  businessLicense?: string;
}

// Simplified Affiliate Partner Admin Registration
export interface AffiliatePartnerAdminRegistrationRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  companyName: string;
  partnerType?: AffiliatePartnerType;
  partnershipType?: PartnershipType;
  address?: string;
  phone?: string;
  businessLicense?: string;
  website?: string;
  description?: string;
}

export interface ProviderRegistrationResponse {
  message: string;
  provider: HealthcareProvider | TourOperator | AffiliatePartner;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
  };
  token: string;
  expiresIn: string;
}